import React, { useEffect } from 'react';
import { Meteor } from 'meteor/meteor';
import { Accounts } from 'meteor/accounts-base';
import { strings } from '/imports/strings';
import { Input } from '/imports/ui/Input';
import { useParams } from 'react-router';
import { Link, useNavigate } from 'react-router-dom';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useSubscription, useFindOne, useUser, useMethod } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Checkbox } from '/imports/ui/Checkbox';
import { createNewUser, deleteUser, getUserLoginJwt, updateUser, userDisplayName } from '/imports/api/users';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { autoForm, formConfig } from '/imports/ui/AutoForm';
import { ImageInput } from '/imports/ui/ImageInput';
import { NotFoundPage } from '/imports/ui/NotFoundPage';
import { Button } from '/imports/ui/Button';
import { getAccountForUser } from '/imports/api/accounts';

const userPageExtraClasses = [
  '[&_.page-heading_.left]:w-full',
  '[&_.page-heading_h1]:flex [&_.page-heading_h1]:w-full [&_.page-heading_h1]:justify-between',
  '[&_.impersonate]:ml-auto',
].join(' ');

const UserPageStyled = (props: React.ComponentProps<typeof Page>) => (
  <Page
    {...props}
    className={`${props.className ? `${props.className} ` : ''}${userPageExtraClasses}`}
  />
);

let redirectToAccountCreation = false;

export const UserPage = () => {
  const navigate = useNavigate();
  const user = useUser();
  const { _id } = useParams<{ _id: string }>();
  const isNew = _id === 'new';
  const documentLoading = useSubscription(Publications.listUsers, { _id });
  const document = useFindOne(() => Meteor.users.find({ _id }), [documentLoading]);
  const [account, loadingAccount] = useMethod(getAccountForUser, _id as string, [_id]);
  useEffect(() => { redirectToAccountCreation = false; }, []);
  const impersonate = async () => {
    const jwt = await getUserLoginJwt.call(_id as string);
    Accounts.callLoginMethod({ methodArguments: [{ jwt }] });
    navigate('/');
  };
  const form = formConfig<Meteor.User>([
    [{ field: 'active', input: { type: Checkbox } }, { field: 'verified', input: { type: Checkbox } }],
    [{ field: 'avatar', label: strings.ProfilePicture(), input: { type: ImageInput, props: { aspect: 1 } } }],
    [{ field: 'username' }, { field: 'password', input: { type: Input, props: { type: 'password' } } }],
    [{ field: 'firstName' }, { field: 'lastName' }, { field: 'email' }, { field: 'phone' }],
    [{ field: 'isSuperUser', input: { type: Checkbox }, label: strings.SuperUser() }],
  ]);
  const [UserForm] = autoForm({
    document,
    createMethod: createNewUser as any,
    updateMethod: updateUser as any,
    deleteMethod: deleteUser,
    form,
    redirect: '/users',
    defaultUpdate: { _id, active: true, verified: true },
    extraButtons: account ? <Link to={`/accounts/${account._id}`}><Button>View Account</Button></Link> : <Button type="submit" onClick={() => { redirectToAccountCreation = true; }}>Save and Create Account</Button>,
    onSave: (_id) => redirectToAccountCreation ? navigate(`/accounts/new?userId=${_id}`) : undefined
  });
  if (documentLoading || loadingAccount) return <LoadingFlex />;
  if (!user?.isSuperUser) return <NotPermittedPage />;
  if (!isNew && !document && !documentLoading) return <NotFoundPage />;
  return (
    <UserPageStyled
      className={`page${!documentLoading ? ' initializing' : ''}`}
      breadcrumbs={[
        { path: '/users', label: strings.Users() },
        { path: `/users/${_id}`, label: !document ? strings.New() : userDisplayName(document) || document._id },
      ]}
    >
      <PageHeading
        left={<h1><span>{isNew ? `${strings.New()} ${strings.User()}` : `${strings.Edit()} ${strings.User()}`}</span>{isNew ? null : <Button className="primary impersonate" onClick={() => impersonate()}>{strings.Impersonate()}</Button>}</h1>}
      />
      <PageBody>
        {UserForm}
      </PageBody>
    </UserPageStyled>
  );
};